import { type JSX } from "react";

export type PalimpsestHeaderProps = {
  /** モーダル title 表示 (省略時は「世界線の palimpsest」)。 */
  title?: string;
  /** 閉じるボタンのクリックで呼ばれる。 */
  onClose: () => void;
  /** 世界情報パネル起動ボタンのクリックで呼ばれる。未指定時はボタン非表示。 */
  onToggleWorldInfo?: () => void;
  /** 世界情報パネルが開いているか (ボタンの aria-pressed 反映用)。 */
  worldInfoOpen?: boolean;
};

/**
 * worldline-palimpsest (Phase 3-1, Task 5.3) palimpsest modal のヘッダ。
 *
 * 閉じるボタン + title + 世界情報パネル起動ボタンの 3 要素を横並びで配置する。
 * 既存 modal pattern (BackscrollView 等) と視覚的に揃える。
 */
export function PalimpsestHeader({
  title,
  onClose,
  onToggleWorldInfo,
  worldInfoOpen = false,
}: PalimpsestHeaderProps): JSX.Element {
  return (
    <header
      data-testid="palimpsest-header"
      className="flex items-center justify-between gap-2 px-3 py-2 border-b border-slate-700/60 bg-slate-900/60"
    >
      <button
        type="button"
        data-testid="palimpsest-header-close"
        onClick={onClose}
        aria-label="palimpsest を閉じる"
        className="text-slate-400 hover:text-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 rounded px-1"
      >
        ✕
      </button>
      <h2
        data-testid="palimpsest-header-title"
        className="text-sm font-semibold text-slate-200 truncate"
      >
        {title ?? "世界線の palimpsest"}
      </h2>
      {onToggleWorldInfo !== undefined ? (
        <button
          type="button"
          data-testid="palimpsest-header-worldinfo-toggle"
          onClick={onToggleWorldInfo}
          aria-pressed={worldInfoOpen}
          aria-label="世界情報パネルを切り替える"
          className={[
            "text-xs px-2 py-0.5 rounded border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-500",
            worldInfoOpen
              ? "border-indigo-500 bg-indigo-900/40 text-indigo-200"
              : "border-slate-600 bg-slate-800/40 text-slate-300 hover:bg-slate-700",
          ].join(" ")}
        >
          世界情報
        </button>
      ) : (
        <span />
      )}
    </header>
  );
}
